/**
 * Velociraptor Template Step
 *
 * Step within the Collection Wizard where the user selects a saved
 * Velociraptor offline collector template or creates a new one via
 * the VelociraptorConfigModal.
 */

import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import type { ArtefactTemplate } from '../../types/artefact-templates'
import type { TemplateUserRole } from '../../utils/template-permissions'
import { VelociraptorConfigModal } from '../velociraptor/VelociraptorConfigModal'
import {
  TemplateGroup,
  TemplateStepEmpty,
  TemplateStepLoading,
  type ScopeIcon,
} from './velociraptor-template-step/velociraptor-template-step.parts'

interface VelociraptorTemplateStepProps {
  templates: ArtefactTemplate[]
  selectedTemplateId: string | null
  onSelect: (templateId: string | null) => void
  loading?: boolean
  caseId: string
  userRole: TemplateUserRole
  onTemplatesChanged: () => void
}

interface GroupConfig {
  scope: 'case' | 'organisation' | 'system'
  scopeIcon: ScopeIcon
  labelKey: string
}

const GROUPS: readonly GroupConfig[] = [
  { scope: 'case', scopeIcon: 'case', labelKey: 'collections.artefactTemplates.caseTemplates' },
  {
    scope: 'organisation',
    scopeIcon: 'org',
    labelKey: 'collections.artefactTemplates.orgTemplates',
  },
  {
    scope: 'system',
    scopeIcon: 'system',
    labelKey: 'collections.artefactTemplates.systemTemplates',
  },
]

export function VelociraptorTemplateStep({
  templates,
  selectedTemplateId,
  onSelect,
  loading,
  caseId,
  userRole,
  onTemplatesChanged,
}: VelociraptorTemplateStepProps): ReactElement {
  const { t } = useTranslation('cases')
  const translate = t as (key: string) => string
  const [modalOpen, setModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ArtefactTemplate | null>(null)

  const openCreate = (): void => {
    setEditingTemplate(null)
    setModalOpen(true)
  }
  const openEdit = (template: ArtefactTemplate): void => {
    setEditingTemplate(template)
    setModalOpen(true)
  }
  const handleSaved = (template: ArtefactTemplate): void => {
    setModalOpen(false)
    setEditingTemplate(null)
    onSelect(template.id)
    onTemplatesChanged()
  }
  const handleClose = (): void => {
    setModalOpen(false)
    setEditingTemplate(null)
  }

  if (loading) return <TemplateStepLoading />

  return (
    <div className="space-y-4">
      <button
        onClick={openCreate}
        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-4 text-sm font-medium text-slate-600 transition-colors hover:border-fuchsia-400 hover:text-fuchsia-600 dark:border-slate-600 dark:text-slate-400 dark:hover:border-fuchsia-600 dark:hover:text-fuchsia-400"
      >
        <Plus className="h-4 w-4" />
        {t('velociraptor.templateStep.createNew', 'Create New Offline Collector Template')}
      </button>
      {GROUPS.map((group) => (
        <TemplateGroup
          key={group.scope}
          label={translate(group.labelKey)}
          templates={templates.filter((tpl) => tpl.scope === group.scope)}
          selectedId={selectedTemplateId}
          scopeIcon={group.scopeIcon}
          onSelect={onSelect}
          onEdit={openEdit}
          t={t}
        />
      ))}
      {templates.length === 0 && (
        <TemplateStepEmpty
          noTemplatesLabel={t(
            'velociraptor.templateStep.noTemplates',
            'No templates yet. Create one to get started.'
          )}
        />
      )}
      <VelociraptorConfigModal
        isOpen={modalOpen}
        onClose={handleClose}
        onTemplateSaved={handleSaved}
        editTemplate={editingTemplate}
        context={{ scope: 'case', caseId, userRole }}
      />
    </div>
  )
}
